import { fileVariantSrcSet, fileVariantUrl, type FileVariantKind } from '@brimo/shared';

/**
 * Pure helpers of the „Bilder" gallery (property and unit records): ordering, moving, the cover and the
 * images shown in the record's image slots. The server keeps `position` and `isCover`; the client only
 * sends the new order (`PUT …/media/order`) or the cover flag (`PATCH /media/:id`).
 */

export interface GalleryItem {
  id: string;
  kind: 'photo' | 'floor_plan' | 'other';
  position: number;
  isCover: boolean;
}

/** Position order, ties by id so that the order is stable between renders. */
export function sortGallery<T extends GalleryItem>(items: readonly T[]): T[] {
  return [...items].sort((a, b) => a.position - b.position || a.id.localeCompare(b.id));
}

/** The ids after moving the item one step (`-1` towards the start, `1` towards the end); unchanged at the edges. */
export function moveItem<T extends GalleryItem>(items: readonly T[], id: string, step: -1 | 1): string[] {
  const ids = sortGallery(items).map((item) => item.id);
  const from = ids.indexOf(id);
  const to = from + step;
  if (from < 0 || to < 0 || to >= ids.length) return ids;
  const next = [...ids];
  next.splice(from, 1);
  next.splice(to, 0, id);
  return next;
}

/** Applies an order of ids optimistically: positions 0…n-1 in that order, items not named keep their place at the end. */
export function applyOrder<T extends GalleryItem>(items: readonly T[], ids: readonly string[]): T[] {
  const rank = new Map(ids.map((id, index) => [id, index]));
  const sorted = sortGallery(items);
  const named = sorted.filter((item) => rank.has(item.id));
  const rest = sorted.filter((item) => !rank.has(item.id));
  named.sort((a, b) => (rank.get(a.id) ?? 0) - (rank.get(b.id) ?? 0));
  return [...named, ...rest].map((item, index) => ({ ...item, position: index }));
}

/** Exactly one cover after setting `id` (optimistic update of „Als Titelbild"). */
export function withCover<T extends GalleryItem>(items: readonly T[], id: string): T[] {
  return items.map((item) => ({ ...item, isCover: item.id === id }));
}

/** The cover photo, or the first photo when none is marked; floor plans are never the cover fallback. */
export function coverItem<T extends GalleryItem>(items: readonly T[]): T | null {
  const sorted = sortGallery(items);
  return sorted.find((item) => item.isCover) ?? sorted.find((item) => item.kind === 'photo') ?? null;
}

/** The first floor plan in gallery order (the unit record's „Grundriss" slot). */
export function floorPlanItem<T extends GalleryItem>(items: readonly T[]): T | null {
  return sortGallery(items).find((item) => item.kind === 'floor_plan') ?? null;
}

/** Index after a keyboard step in the lightbox, wrapping around. */
export function stepIndex(index: number, step: -1 | 1, length: number): number {
  if (length === 0) return 0;
  return (index + step + length) % length;
}

/** `src` + `srcset` of a stored picture through its WebP variants (`GET /api/files/:id?variant=`). */
export interface PictureSource {
  src: string;
  srcSet: string;
}

/**
 * The picture of a file: `src` is the variant `fallback` (for browsers without `srcset`), `srcSet` the variants up to
 * `upTo` with their real widths when the size is known (media items), else the nominal 400/1200/2400 px.
 */
export function pictureOf(
  fileId: string,
  options: {
    size?: { width: number | null; height: number | null } | undefined;
    upTo?: FileVariantKind | undefined;
    fallback?: FileVariantKind | undefined;
  } = {},
): PictureSource {
  const upTo = options.upTo ?? 'large';
  return {
    src: fileVariantUrl(fileId, options.fallback ?? 'medium'),
    srcSet: fileVariantSrcSet(fileId, options.size ?? { width: null, height: null }, upTo),
  };
}

/** Layout widths (`sizes`) of the picture slots, so the browser fetches the smallest sufficient variant. */
export const PICTURE_SIZES = {
  /** Gallery grid: 4 columns in the record card, 2 on mobile. */
  galleryTile: '(max-width: 767px) 50vw, 240px',
  /** Hero of the property record: the full content width. */
  hero: '100vw',
  /** Overview tiles (3 columns, 1 on mobile). */
  listTile: '(max-width: 767px) 100vw, 33vw',
  /** Unit record image slots (2 columns, 1 on mobile). */
  unitSlot: '(max-width: 767px) 100vw, 50vw',
  /** Lightbox stage (dialog 960 px). */
  lightbox: '(max-width: 767px) 100vw, 920px',
} as const;
